import React, { useRef, useState } from 'react';
import axiosInstance from '../../api/axiosConfig';

// The documents filed against one payment — invoices, receipts, anything. They
// live in SharePoint (one folder per service) and are fetched through the API,
// so they open as blobs rather than SharePoint links. A payment recorded before
// documents existed may still carry a single legacy `receiptFile`.
const openBlob = (data, type, name) => {
    const blob = new Blob([data], { type: type || data.type });
    const url = URL.createObjectURL(blob);
    if ((blob.type || '').startsWith('image/') || blob.type === 'application/pdf') {
        window.open(url, '_blank');
    } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
};

const PaymentDocuments = ({ payment, subscriptionId, canEdit, onChanged }) => {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const base = `/api/subscriptions/${subscriptionId}/payments/${payment._id}/documents`;
    const docs = payment.documents || [];
    const legacy = payment.receiptFile?.filename ? payment.receiptFile : null;

    const view = async (doc) => {
        try {
            const res = await axiosInstance.get(`${base}/${doc._id}/content`, { responseType: 'blob' });
            openBlob(res.data, doc.fileType, doc.fileName);
        } catch {
            alert('Failed to open document');
        }
    };

    const viewLegacy = async () => {
        try {
            const res = await axiosInstance.get(`/api/files/subscription-receipts/${encodeURIComponent(legacy.filename)}`, { responseType: 'blob' });
            openBlob(res.data, legacy.mimetype, legacy.originalName || legacy.filename);
        } catch {
            alert('Failed to open receipt');
        }
    };

    const upload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;
        const data = new FormData();
        files.forEach((f) => data.append('files', f));
        setBusy(true);
        try {
            await axiosInstance.post(base, data, { headers: { 'Content-Type': 'multipart/form-data' } });
            onChanged && onChanged();
        } catch (err) {
            alert(err.response?.data?.message || 'Upload failed');
            onChanged && onChanged();
        } finally {
            setBusy(false);
        }
    };

    const remove = async (doc) => {
        if (!window.confirm(`Delete "${doc.fileName}"? It goes to the SharePoint recycle bin.`)) return;
        try {
            await axiosInstance.delete(`${base}/${doc._id}`);
            onChanged && onChanged();
        } catch (err) {
            alert(err.response?.data?.message || 'Delete failed');
        }
    };

    return (
        <div className="payment-docs">
            {docs.map((doc) => (
                <div key={doc._id} className="payment-doc">
                    <button type="button" className="btn btn-sm btn-secondary" title={doc.fileName} onClick={() => view(doc)}>📎</button>
                    {canEdit && (
                        <button type="button" className="btn btn-sm btn-ghost" title="Delete document" onClick={() => remove(doc)}>×</button>
                    )}
                </div>
            ))}
            {legacy && (
                <button type="button" className="btn btn-sm btn-secondary" title={legacy.originalName || 'Receipt'} onClick={viewLegacy}>📎</button>
            )}
            {canEdit && (
                <>
                    <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => inputRef.current?.click()}>
                        {busy ? 'Uploading…' : 'Attach'}
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                        onChange={upload}
                    />
                </>
            )}
            {!canEdit && !docs.length && !legacy && '—'}
        </div>
    );
};

export default PaymentDocuments;
